import useRestService from '../shared/rest-service';
import User from '../models/user';
import * as React from 'react';
import Client from '../models/client';
import useClient from './useClient';
import Application from '../models/application';

/**
 * useUser
 * @function useUser - Custom hook for the user management page
 */
const useUser = () => {
  const { getAuth, postAuth, putAuth } = useRestService();

  /**
   * @function createUser - Create a new user
   * @param user - the new user to create
   */
  const createUser = (user: User) => {
    return postAuth('api/users', user);
  };

  const [loadingAssociatedData, setLoadingAssociatedData] = React.useState<boolean>(true);
  const [clients, setClients] = React.useState<Client[]>([]);
  const [applications, setApplications] = React.useState<Application[]>([]);
  const [currentUserRole, setCurrentUserRole] = React.useState<string>('');
  const { fetchAdminClients } = useClient();

  /**
   * @function fetchAssociatedData - Get extra data needed to create/edit a user
   */
  const fetchAssociatedData = () => {
    if (!loadingAssociatedData) setLoadingAssociatedData(true);
    Promise.all([fetchAdminClients(1, 0), fetchApplications()]).then(([clients, applications]) => {
      setClients(clients?.results || []);
      setApplications(applications);
      setLoadingAssociatedData(false);
    });
  };

  const fetchCurrentUserRole = () => {
    if (!loadingAssociatedData) {
      setLoadingAssociatedData(true);
    }
    getCurrentUser().then((currentUser) => {
      setCurrentUserRole(currentUser.role);
      setLoadingAssociatedData(false);
    });
  };

  const fetchApplications = (): Promise<Application[]> => {
    return getAuth(`api/management/applications`);
  };

  const fetchUser = (userId: string): Promise<User> => {
    return getAuth(`api/users/${userId}`).then((user: User) => {
      return user;
    });
  };

  const editUser = (user: User): Promise<any> => {
    return putAuth(`api/users/${user.id}`, user);
  };

  const inviteUser = (user: User): Promise<any> => {
    return postAuth(`api/users/${user.id}/invite`, user);
  };

  const getCurrentUser = (): Promise<User> => {
    return getAuth(`api/users/current`);
  };

  return {
    createUser,
    loadingAssociatedData,
    clients,
    applications,
    currentUserRole,
    fetchCurrentUserRole,
    fetchAssociatedData,
    fetchUser,
    editUser,
    inviteUser,
    getCurrentUser
  };
};

export default useUser;
